import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { checkChatLink, connectChat, ApiError, type AuthenticatedSession, type ChatLinkCheck } from '../api'
import { ThemeToggle } from '../components/ThemeToggle'
import { Wordmark } from '../components/Wordmark'

/**
 * The one screen a chat sends a person to. They are already signed in — the
 * shell only renders this once a session exists — and this page asks them to
 * let that chat act as them at one organization. The chat never sees a key.
 *
 * Everything on the link is chosen by whoever made it. The server sends the
 * code only to a chat host it knows, or to a chat on the person's own
 * computer, but a link anybody sends can still name one of those. The page
 * therefore shows where the link asks for the code to go, which is the one
 * thing that says who will hold it, and gives the chat's name only as what
 * it calls itself, beside that host. The name is the one the chat registered
 * under the link's client_id, as the server reports it, and never one the
 * link carries: anybody can write a name into a link, and the grant was
 * named from the page's copy of it (review-3-docs R1). Approval of each send is the chat's own step (an MCP
 * elicitation the chat answers), so the page says the chat asks rather than
 * that docsdone makes it ask. Cancel goes home: sending the browser to an
 * address nobody has vouched for made it an open redirect from
 * app.docsdone.co.
 */
export function Connect({ session }: { session: AuthenticatedSession }) {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const clientID = params.get('client_id') ?? ''
  const redirect = params.get('redirect_uri') ?? ''
  const state = params.get('state') ?? ''
  const challenge = params.get('code_challenge') ?? ''
  const method = params.get('code_challenge_method') || 'S256'
  const askedOrg = params.get('organization_id') ?? ''
  const responseType = params.get('response_type') ?? ''

  const memberships = session.memberships
  const only = memberships.length === 1 ? memberships[0].organization_id : ''
  const preset = memberships.some((m) => m.organization_id === askedOrg) ? askedOrg : ''
  const [chosen, setChosen] = useState(only || preset)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Set by a refusal that pressing Allow again cannot change.
  const [refused, setRefused] = useState(false)

  const org = memberships.find((m) => m.organization_id === chosen)
  const ready = useMemo(
    () => redirectAllowed(redirect) && stateOk(state) && challengeOk(challenge) && (method === '' || method === 'S256'),
    [redirect, state, challenge, method],
  )
  const returnHost = ready ? new URL(redirect).host : ''
  // Allow refuses the service credential before it looks at the link, so the
  // link's answer is never asked for and never shown (review-3 web-4).
  const operator = session.auth_method === 'service_credential'
  // The server's answer to whether Allow would accept this link: null while
  // asking, 'unknown' when the question got no answer.
  const [check, setCheck] = useState<LinkCheck | null>(null)
  useEffect(() => {
    if (!ready || operator) return
    let live = true
    checkChatLink(clientID, redirect, responseType)
      .then((c) => { if (live) setCheck(c) })
      .catch(() => { if (live) setCheck('unknown') })
    return () => { live = false }
  }, [ready, operator, clientID, redirect, responseType])
  const link = linkState(check, operator)
  // A refusal that pressing Allow again cannot change, on load or after a press.
  const closed = refused || (link.allowOff && link.refusal !== null)
  const person = personNamed(session.identity.display_name, session.identity.email)

  async function allow() {
    if (!org) return
    setBusy(true)
    setError('')
    try {
      const res = await connectChat({
        redirect_uri: redirect,
        state,
        code_challenge: challenge,
        code_challenge_method: method || 'S256',
        client_id: clientID,
        response_type: responseType || undefined,
        organization_id: org.organization_id,
      })
      window.location.assign(res.redirect_uri)
    } catch (e) {
      const refusal = connectRefusal(e)
      setError(refusal.message)
      setRefused(refusal.final)
      setBusy(false)
    }
  }

  function decline() {
    navigate('/')
  }

  return (
    <div className="signin-wrap">
      <div className="card signin-card connect-card">
        <div className="signin-logo">
          <Wordmark />
        </div>
        <ThemeToggle />
        <h1>Allow this chat</h1>
        {!ready ? (
          <p className="small muted">
            This link is missing a return address the chat can use. Go back to the chat and start the connection again.
          </p>
        ) : memberships.length === 0 ? (
          <p className="small muted">You are not a member of an organization, so there is nothing for a chat to use.</p>
        ) : (
          <>
            <p>
              {askingChat(check, returnHost)} is asking to use docsdone as {person}
              {org ? ` at ${org.organization_name}` : ''}.
            </p>
            {closed ? (
              // A final refusal: nothing on the card can lead to a connection,
              // so it asks for no organization and describes no connection,
              // and the refusal sits under the request, above the one way
              // out (review-3 design-2).
              <div className="stack">
                <div id="connect-refused" className="notice notice-bad">{link.refusal ?? error}</div>
                <button className="btn btn-secondary btn-block" type="button" onClick={decline}>
                  Cancel
                </button>
              </div>
            ) : (
              <>
                {link.warning && <ReturnWarning host={returnHost} known={link.warning === 'accepted'} />}
                {memberships.length > 1 && (
                  <fieldset className="radio-group">
                    <legend>Organization</legend>
                    {memberships.map((m) => (
                      <label key={m.organization_id} className="check">
                        <input
                          type="radio"
                          name="organization"
                          checked={chosen === m.organization_id}
                          onChange={() => setChosen(m.organization_id)}
                        />
                        <span>{m.organization_name}</span>
                      </label>
                    ))}
                  </fieldset>
                )}
                {memberships.length > 1 && !org && (
                  <p className="small muted">Choose an organization before allowing access.</p>
                )}
                <div className="grid-2">
                  <div>
                    <h2>It can</h2>
                    <ul>
                      <li>See documents, templates, and how many completions are left.</li>
                      <li>Send, remind, withdraw, or replace a link. The chat asks you before each of these; docsdone cannot check that it does.</li>
                    </ul>
                  </div>
                  <div>
                    <h2>It cannot</h2>
                    <ul>
                      <li>Sign a document. The signer opens the link docsdone emails them.</li>
                      <li>Change the plan, invite people, or turn on single sign-on.</li>
                    </ul>
                  </div>
                </div>
                <p className="small muted connect-note">
                  You can disconnect it later on your account page, and an administrator can in organization settings.
                </p>
                {/* In the stack, so the refusal does not touch Allow. */}
                <div className="stack">
                  {(link.refusal ?? error) && <div className="notice notice-bad">{link.refusal ?? error}</div>}
                  <button className="btn btn-block" type="button" disabled={busy || !org || link.allowOff} onClick={() => void allow()}>
                    {busy ? 'Connecting…' : 'Allow access'}
                  </button>
                  <button className="btn btn-secondary btn-block" type="button" onClick={decline}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Who the chat would act as: the name and the address, each once. A person
 * who signed in with a code has the address as their name, and the page said
 * "as a@x (a@x)".
 */
export function personNamed(name: string, email?: string): string {
  const n = name.trim()
  const e = (email ?? '').trim()
  if (!e || n.toLowerCase() === e.toLowerCase()) return n || e
  if (!n) return e
  return `${n} (${e})`
}

/** The check's answer as the page holds it. */
export type LinkCheck = ChatLinkCheck | 'unknown'

/**
 * What the page shows for the server's answer to the link, before anything is
 * pressed. While it asks, nothing is said about the return address and Allow
 * waits. A refusal is shown at once, as the refusal Allow would give, and
 * Allow is off: pressing it could only be refused. An accepted link gets the
 * plain warning that docsdone sends the person there. A check that got no
 * answer leaves Allow on, since the server checks again when it is pressed,
 * and the warning is worded so it is true whether or not the host is known.
 *
 * Somebody signed in with the operator credential is refused by Allow before
 * the link is looked at, whatever the check says of the link: the page told
 * them docsdone sends them to a host, for a send that could not happen
 * (review-3 web-4).
 */
export function linkState(check: LinkCheck | null, operator = false): {
  warning: 'accepted' | 'unknown' | null
  refusal: string | null
  allowOff: boolean
} {
  if (operator) return { warning: null, refusal: refusalFor('service_credential').message, allowOff: true }
  if (check === null) return { warning: null, refusal: null, allowOff: true }
  if (check === 'unknown') return { warning: 'unknown', refusal: null, allowOff: false }
  if (check.accepted) return { warning: 'accepted', refusal: null, allowOff: false }
  const refusal = refusalFor(check.code)
  return { warning: null, refusal: refusal.message, allowOff: refusal.final }
}

/**
 * Where the link asks docsdone to send the person, and who can act as them
 * once it does. Once the server has said it accepts the address, the page
 * says plainly that docsdone sends the person there. Without that answer the
 * page cannot tell which hosts the server accepts, so the sentence says what
 * the link asks for and that docsdone does it only for a chat it knows, which
 * is true for a host it refuses as well as for one it accepts. Once a refusal
 * is final the warning goes, since nothing will be sent.
 */
export function ReturnWarning({ host, known }: { host: string; known: boolean }) {
  return (
    <div className="notice notice-warn connect-return" id="connect-return">
      <p>
        {known ? (
          <>If you allow it, docsdone sends you to <strong>{host}</strong>, and whoever runs that address can act as you.</>
        ) : (
          <>
            This link asks docsdone to send you to <strong>{host}</strong> once you allow it. docsdone does that only
            for the chats it knows, and whoever runs that address can then act as you.
          </>
        )}
      </p>
      <p>Allow it only if you started this from your own chat, just now.</p>
    </div>
  )
}

/**
 * What the page says when Allow is refused, and whether pressing it again can
 * help. The server sends a connection only to a chat host it knows (and to a
 * chat on the person's own computer), and refuses any other return address
 * with bad_redirect, and a link that asks for anything but a code with
 * unsupported_response_type. Those refusals are final for this link: Allow is
 * then turned off, and the sentence says the chat cannot be connected, rather
 * than leaving a button that can only be refused again.
 */
export function connectRefusal(e: unknown): { message: string; final: boolean } {
  if (e instanceof ApiError) {
    // The 50-chat refusal is the one sentence here the server words: it says
    // how long a waiting code holds its place, and only the server knows
    // that (store.MCPCodeTTL), so its sentence is shown as it comes. It is
    // the page's own sentence word for word since R8-server (3783fb2), and
    // the page's copy stays for an answer that carried none.
    if (e.code === 'too_many_chats') return { message: e.message.trim() && !NO_SENTENCE.test(e.message) ? e.message : TOO_MANY_CHATS, final: false }
    return refusalFor(e.code)
  }
  return { message: 'Could not connect that chat.', final: false }
}

/** handle()'s words for an answer whose body carried no sentence. */
const NO_SENTENCE = /^Request failed \(\d+\)$/

function refusalFor(code: string): { message: string; final: boolean } {
  // bad_redirect is two refusals on the server (mcpLinkRefusal): a host it
  // does not send connections to, and an address this chat did not register,
  // on a host it does. The page's sentence named only the first, so a chat on
  // a known host was told its host was unknown (review 4, design-3). It now
  // says what holds for both, and unregistered_redirect, once the server
  // answers it for the second, gets its own sentence.
  switch (code) {
    case 'service_credential':
      return { message: 'The operator credential cannot connect a chat.', final: true }
    case 'no_session':
      return { message: 'Sign in again before connecting a chat.', final: false }
    case 'bad_redirect':
      return {
        message: 'docsdone does not send this chat\'s connection to that return address, so the chat cannot be connected from this link.',
        final: true,
      }
    case 'unregistered_redirect':
      return {
        message: 'This chat did not register that return address, so it cannot be connected from this link. Start the connection again from the chat.',
        final: true,
      }
    case 'unsupported_response_type':
      return {
        message: 'This link asks for a kind of connection docsdone does not make, so this chat cannot be connected from it.',
        final: true,
      }
    case 'bad_state':
      return { message: 'The connection is missing its state value.', final: true }
    case 'bad_challenge':
      return { message: 'The connection needs an S256 code challenge.', final: true }
    case 'organization_required':
      return { message: 'Choose an organization.', final: false }
    case 'not_a_member':
      return { message: 'You are not a member of that organization.', final: false }
    // One person holds at most 50 chats, connected or waiting to connect,
    // across every organization; ending one lets Allow access work again, so
    // the refusal is not final for this link. The server's sentence once sent
    // them to "Connected chats in Settings": Settings is the organization's
    // page, shown only to an administrator and listing that organization's
    // chats. A person's own chats, the ones the bound counts, are on their
    // account page, and a code nobody used is listed nowhere but lapses in 10
    // minutes (store.MCPCodeTTL) (review 6, web-5 and design-3). connectRefusal
    // shows the server's sentence, which is now this one (R8-server).
    case 'too_many_chats':
      return {
        message: TOO_MANY_CHATS,
        final: false,
      }
  }
  return { message: 'Could not connect that chat.', final: false }
}

/**
 * Who is asking, as the request sentence opens. The host its connection goes
 * to comes first, because it says who will hold the grant; the name is the
 * one the check reports the chat registered, given only as what it calls
 * itself, since anybody can register any name. The name is set apart in its
 * own element, which no name can close: the server keeps quotation marks and
 * parentheses in a name, and a name inside the page's own quotes, with the
 * host after it, could close the quote and write a host of its own in the
 * same form (review 4, design-7).
 *
 * Until the check has answered, or when it refused or got no answer, no name
 * is known and the chat is only "A chat". The server calls a chat it has no
 * name for "A chat" too: one that registered none, but also one whose
 * registration it removed for going unused, or one that registered before
 * docsdone kept names. The page cannot tell these apart, so it says only
 * that docsdone has no name for the chat (review 2, web-6; review 4, web-5).
 */
export function askingChat(check: LinkCheck | null, host: string): ReactNode {
  if (check === null || check === 'unknown' || !check.accepted) return 'A chat'
  const name = (check.client_name ?? '').trim()
  const at = host ? <> at <strong>{host}</strong></> : null
  if (!name || name === UNNAMED_CHAT) return <>A chat{at} that docsdone has no name for</>
  return <>A chat{at} that calls itself <strong>{name}</strong></>
}

/** The 50-chat refusal, pointing at the page every person has. */
export const TOO_MANY_CHATS =
  'You have too many chats connected, or waiting to connect. End one you no longer use under Connected chats on your account page, then press Allow access again. A chat still waiting to connect frees its place within 10 minutes.'

/** The server's name for a chat nothing names (internal/api/mcp_connect.go unnamedChat). */
const UNNAMED_CHAT = 'A chat'

function redirectAllowed(raw: string): boolean {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return false
  }
  if (!url.hostname || url.username || url.password || url.hash) return false
  if (url.protocol === 'https:') return true
  if (url.protocol !== 'http:') return false
  return url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]' || url.hostname === '::1'
}

function challengeOk(value: string): boolean {
  return /^[A-Za-z0-9_-]{43,128}$/.test(value)
}

function stateOk(value: string): boolean {
  return value.length > 0 && value.length <= 512 && !/[\r\n]/.test(value)
}
